import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getPlanFromProductId, PlanKey } from "@/lib/stripeTiers";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  plan: PlanKey;
  subscriptionLoading: boolean;
  subscriptionEnd: string | null;
  upcomingPlan: string | null;
  upcomingStartDate: string | null;
  upcomingEndDate: string | null;
  pausedPlan: string | null;
  pausedRemainingDays: number | null;
  totalSearches: number;
  dailySearches: number;
  isAdmin: boolean;
  isBroker: boolean;
  approvalStatus: string;
  isApproved: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
  incrementSearch: () => Promise<{ allowed: boolean; reason: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<PlanKey>("free");
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [upcomingPlan, setUpcomingPlan] = useState<string | null>(null);
  const [upcomingStartDate, setUpcomingStartDate] = useState<string | null>(null);
  const [upcomingEndDate, setUpcomingEndDate] = useState<string | null>(null);
  const [pausedPlan, setPausedPlan] = useState<string | null>(null);
  const [pausedRemainingDays, setPausedRemainingDays] = useState<number | null>(null);
  const [totalSearches, setTotalSearches] = useState(0);
  const [dailySearches, setDailySearches] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBroker, setIsBroker] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("pending");
  const [isApproved, setIsApproved] = useState(false);

  // Check subscription status
  const checkSubscription = useCallback(async () => {
    if (!user) return;
    setSubscriptionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      if (data?.suspended) {
        toast.error("Your account has been suspended. Please contact support.");
        await supabase.auth.signOut();
        return;
      }
      if (data?.subscribed && data?.product_id) {
        setPlan(getPlanFromProductId(data.product_id));
        setSubscriptionEnd(data.subscription_end || null);
        setUpcomingPlan(data.upcoming_plan || null);
        setUpcomingStartDate(data.upcoming_start_date || null);
        setUpcomingEndDate(data.upcoming_end_date || null);
        setPausedPlan(data.paused_plan || null);
        setPausedRemainingDays(data.paused_remaining_days || null);
      } else {
        setPlan("free");
        setSubscriptionEnd(null);
        setUpcomingPlan(null);
        setUpcomingStartDate(null);
        setUpcomingEndDate(null);
        setPausedPlan(null);
        setPausedRemainingDays(null);
      }
    } catch {
      // Silent fail — default to free
    } finally {
      setSubscriptionLoading(false);
    }
  }, [user]);

  // Load search counts
  const loadSearchCounts = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("search_counts")
      .select("total_searches, daily_searches")
      .eq("user_id", user.id)
      .maybeSingle();
    if (data) {
      setTotalSearches(data.total_searches);
      setDailySearches(data.daily_searches);
    }
  }, [user]);

  // Increment search count via RPC
  const incrementSearch = useCallback(async (): Promise<{ allowed: boolean; reason: string }> => {
    if (!user) return { allowed: false, reason: "not_authenticated" };
    const { data, error } = await supabase.rpc("increment_search_count", { p_user_id: user.id });
    if (error) return { allowed: false, reason: "error" };
    const result = data as any;
    setTotalSearches(result.total_searches);
    setDailySearches(result.daily_searches);
    return { allowed: result.allowed, reason: result.reason || "" };
  }, [user]);

  // Single device enforcement
  const enforceSession = useCallback(async (currentSession: Session | null) => {
    if (!currentSession?.user) return;
    const sessionToken = currentSession.access_token;
    const userId = currentSession.user.id;

    // Try to upsert our session
    const { data: existing } = await supabase
      .from("active_sessions")
      .select("session_token")
      .eq("user_id", userId)
      .maybeSingle();

    if (existing && existing.session_token !== sessionToken) {
      // Another device was active — we're the new device, update the token
    }

    // Upsert current session
    await supabase.from("active_sessions").upsert(
      {
        user_id: userId,
        session_token: sessionToken,
        device_info: navigator.userAgent,
      },
      { onConflict: "user_id" }
    );
  }, []);

  // Check if our session is still the active one
  const checkActiveSession = useCallback(async () => {
    if (!session?.user) return;
    const { data } = await supabase
      .from("active_sessions")
      .select("session_token")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (data && data.session_token !== session.access_token) {
      toast.error("Your account was logged in from another device. This session has been securely logged out.");
      await supabase.auth.signOut();
    }
  }, [session]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // On user change: check subscription, load counts, enforce session
  useEffect(() => {
   if (user) {
      checkSubscription();
      loadSearchCounts();
      // Check roles
      supabase.from("user_roles").select("role").eq("user_id", user.id)
        .then(({ data }) => {
          const roles = data?.map(r => r.role) || [];
          setIsAdmin(roles.includes("admin"));
          setIsBroker(roles.includes("broker"));
        });
      // Load approval status
      supabase.from("profiles").select("approval_status, is_approved").eq("user_id", user.id).maybeSingle()
        .then(({ data }) => {
          if (data) {
            setApprovalStatus(data.approval_status || "pending");
            setIsApproved(data.is_approved || false);
          }
        });
      // Save pending mobile number from signup
      const pendingMobile = localStorage.getItem("pending_mobile_number");
      if (pendingMobile) {
        supabase.from("profiles").update({ mobile_number: pendingMobile }).eq("user_id", user.id)
          .then(() => localStorage.removeItem("pending_mobile_number"));
      }
    } else {
      setPlan("free");
      setSubscriptionEnd(null);
      setUpcomingPlan(null);
      setUpcomingStartDate(null);
      setUpcomingEndDate(null);
      setPausedPlan(null);
      setPausedRemainingDays(null);
      setTotalSearches(0);
      setDailySearches(0);
      setIsAdmin(false);
      setIsBroker(false);
      setApprovalStatus("pending");
      setIsApproved(false);
    }
  }, [user, checkSubscription, loadSearchCounts]);

  // Enforce single device on session change
  useEffect(() => {
    if (session) {
      enforceSession(session);
    }
  }, [session, enforceSession]);

  // Periodic session check (every 30s)
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(checkActiveSession, 30000);
    return () => clearInterval(interval);
  }, [session, checkActiveSession]);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    if (user) {
      await supabase.from("active_sessions").delete().eq("user_id", user.id);
    }
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{
      user, session, loading, plan, subscriptionLoading, subscriptionEnd,
      upcomingPlan, upcomingStartDate, upcomingEndDate, pausedPlan, pausedRemainingDays,
      totalSearches, dailySearches, isAdmin, isBroker, approvalStatus, isApproved,
      signUp, signIn, signOut, checkSubscription, incrementSearch,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
